/**
 * Lead detail, distribution timeline and the raw request, as tabs over one
 * fetch (plan D7). The Alpine app has these as two separate top-level modals
 * sharing a single `modal` state variable, so they are mutually exclusive: you
 * close one to open the other, about the same lead, each fetching again.
 *
 * The timeline is built from `distribution_data.attempts`, which records every
 * outcome including the ones that never reached a destination. Reading the
 * `group_attempts` table instead renders a filtered or duplicate lead as "no
 * delivery attempts recorded", which is true and useless — the interesting
 * part is the reason, and it lives inline on the lead.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Code, Collapse, Divider, Group, Loader, Modal, ScrollArea, SimpleGrid,
  Stack, Tabs, Text, Timeline, UnstyledButton,
} from '@mantine/core';
import type { ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowBackUp, IconBan, IconBriefcase, IconCheck, IconCopy, IconFilter,
  IconPhoneOff, IconRefresh, IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useCrmPermissions } from './crm/crmShared';
import { SubmissionProperties } from './properties/PropertyFields';
import { NewDealModal } from './crm/DealForms';
import { TimelinePanel } from './crm/Timeline';
import { useLeadDetail, useRedistribute } from '../api/queries';
import { ReturnDialog } from './returns/ReturnDialog';
import { LeadReturnsPanel } from './returns/LeadReturnsPanel';
import { useNavUser } from '../layout/navUser';
import { hasAction, isBuyerLogin, isScopedUser, seesReturns } from '../layout/nav';
import { ApiError } from '../api/client';
import { TOKENS, TYPE, formatDateTime, formatGBP } from '../theme';
import { MonoLabel } from '../ui';
import { titleCase } from '../ui/format';
import { PHONE_STATUS_LABELS, phoneStatusLabel, plainPhoneReason } from './phoneStatus';
import { ScoreBadge, signed } from './scoring/scoringShared';
import { useVocab, type Vocab } from '../vocab';
import { pushRecentLead } from '../layout/search/recentLeads';
import { showsMoney, showsRowType, useStreamOf } from './streams/streamShared';

/** How each outcome should read to someone trying to work out what happened. */
function outcomes(v: Vocab): Record<
  string,
  { colour: string; label: string; icon: typeof IconCheck; blurb: string }
> {
  const lead = v.one('lead');
  const buyer = v.one('buyer');
  const destination = v.one('destination');
  return {
    accepted: { colour: 'teal', label: 'Accepted', icon: IconCheck, blurb: `The ${buyer} accepted this ${lead}.` },
    delivered: { colour: 'teal', label: 'Delivered', icon: IconCheck, blurb: `The ${buyer} accepted this ${lead}.` },
    rejected: { colour: 'red', label: 'Rejected', icon: IconX, blurb: `The ${buyer} was sent this ${lead} and turned it down.` },
    error: { colour: 'red', label: 'Error', icon: IconAlertTriangle, blurb: `The delivery attempt failed before the ${buyer} could answer.` },
    duplicate: { colour: 'orange', label: 'Duplicate', icon: IconCopy, blurb: `Screened out before routing: this ${lead} had already been submitted to the ${v.one('campaign')}.` },
    suppressed: { colour: 'orange', label: 'Suppressed', icon: IconBan, blurb: `Screened out before routing: the ${lead} matched a suppression list.` },
    suppression_error: { colour: 'orange', label: 'Held back', icon: IconAlertTriangle, blurb: `The suppression lists could not be checked, so this ${lead} was not sent here, to be safe. Redistribute it once the check is working again.` },
    cap_error: { colour: 'orange', label: 'Cap not checked', icon: IconAlertTriangle, blurb: `The cap could not be checked, so this ${lead} was not sent here, to be safe.` },
    group_filtered: { colour: 'gray', label: 'Filtered', icon: IconFilter, blurb: `A smart filter on this tier turned the ${lead} away, so no ${destination} was tried.` },
    group_capped: { colour: 'gray', label: 'Capped', icon: IconBan, blurb: `This group had already hit its cap, so no ${destination} was tried.` },
    no_routes: { colour: 'gray', label: 'No routes', icon: IconBan, blurb: `No ${destination} in this tier was eligible for the ${lead}.` },
    skipped: { colour: 'gray', label: 'Skipped', icon: IconBan, blurb: `This ${buyer} already bought the ${lead}, so it was not offered to them again.` },
    validation_failed: { colour: 'orange', label: 'Failed phone check', icon: IconPhoneOff, blurb: `The number did not pass this ${destination}'s phone check, so it was not sent.` },
  };
}

function outcome(result: string, v: Vocab) {
  return (
    outcomes(v)[result] ?? {
      colour: 'gray',
      label: result || 'Unknown',
      icon: IconAlertTriangle,
      blurb: '',
    }
  );
}

/** One label and value, the label in the dashboard's mono. */
function Field({ label, value, wide = false }: { label: string; value: ReactNode; wide?: boolean }) {
  const empty = value === '' || value === null || value === undefined;
  return (
    // `wide` takes the whole row, for values like an email that should not wrap.
    <div style={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
      <MonoLabel style={{ fontSize: 10.5 }}>{label}</MonoLabel>
      <div
        style={{
          fontSize: 13, marginTop: 2, color: empty ? TOKENS.textMuted : TOKENS.text,
          overflowWrap: 'anywhere',
        }}
      >
        {empty ? '—' : value}
      </div>
    </div>
  );
}

/**
 * Custom data keys that repeat what Contact and Routing already show, or are
 * internal ids. Listing them again under Custom fields was noise.
 */
const SHOWN_ABOVE = new Set([
  'campaign_id', 'campaign_name', 'account_id', 'supplier_id', 'supplier_name',
  'buyer_id', 'buyer_name', 'route_id', 'email', 'phone', 'phone1', 'firstname',
  'lastname', 'fullname', 'company', 'source',
]);

type PhoneCheck = NonNullable<
  NonNullable<ReturnType<typeof useLeadDetail>['data']>['distribution']['phone_check']
>;

/**
 * The network's answer for the number, as a coloured status and what network
 * and kind of line it is. Green only for a live number; a dead or absent one
 * is the case worth seeing at a glance.
 */
function PhoneCheckValue({ check }: { check: PhoneCheck | null | undefined }) {
  if (!check?.status) return <span style={{ color: TOKENS.textMuted }}>Not checked</span>;
  // For a number that is not live the provider can put its status code in
  // line_type ("BAD_FORMAT"), which only repeats the badge.
  const lineType = check.line_type && !PHONE_STATUS_LABELS[check.line_type.toUpperCase()]
    ? titleCase(check.line_type.toLowerCase())
    : null;
  const detail = [check.network, lineType]
    .filter(Boolean)
    .join(' · ');
  return (
    <Group gap={6} wrap="wrap">
      <Badge variant="light" color={check.live ? 'chartGreen' : check.status === 'DEAD' ? 'red' : 'orange'}>
        {phoneStatusLabel(check.status)}
      </Badge>
      {detail && <span style={{ color: TOKENS.textSecondary, fontSize: 12.5 }}>{detail}</span>}
      {check.test && <Badge variant="outline" color="gray" size="xs">Test mode</Badge>}
    </Group>
  );
}

/** A titled group of fields, two to a row. */
function FieldGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 10, padding: '12px 14px 14px' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{title}</div>
      <SimpleGrid cols={2} spacing="md" verticalSpacing={12}>{children}</SimpleGrid>
    </div>
  );
}

/** A custom field's value as text: nested values stay readable as JSON. */
function fieldText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** The request and response for one attempt, folded away until asked for. */
function Payloads({ sent, response }: { sent: unknown; response: string | null | undefined }) {
  const [open, setOpen] = useState(false);
  if (sent == null && !response) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <UnstyledButton onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <MonoLabel style={{ color: TOKENS.textSecondary }}>
          {open ? 'Hide' : 'Show'} request and response
        </MonoLabel>
      </UnstyledButton>
      <Collapse in={open}>
        {sent != null && (
          <Stack gap={2} mt="xs">
            <Text size="xs" fw={600} c="dimmed">Request sent</Text>
            <ScrollArea.Autosize mah={180}>
              <Code block>{JSON.stringify(sent, null, 2)}</Code>
            </ScrollArea.Autosize>
          </Stack>
        )}
        {response && (
          <Stack gap={2} mt="xs">
            <Text size="xs" fw={600} c="dimmed">Response</Text>
            <ScrollArea.Autosize mah={180}>
              <Code block>{response}</Code>
            </ScrollArea.Autosize>
          </Stack>
        )}
      </Collapse>
    </div>
  );
}

/** A small figure with its mono label, for the distribution summary. */
function Figure({ label, value, positive = false }: { label: string; value: ReactNode; positive?: boolean }) {
  return (
    <div>
      <MonoLabel style={{ fontSize: 10.5 }}>{label}</MonoLabel>
      <div style={{ ...TYPE.monoFigure, fontSize: 14, marginTop: 2, color: positive ? TOKENS.greenText : TOKENS.text }}>
        {value}
      </div>
    </div>
  );
}

export function LeadDetailModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const v = useVocab();
  const { data, isPending, error } = useLeadDetail(id);
  // Staff with manage_returns return sales; a buyer login asks to.
  const navUser = useNavUser();
  const returnsStaff = !isScopedUser(navUser) && hasAction(navUser, 'manage_returns');
  const buyerLogin = isBuyerLogin(navUser);
  // A buyer login shows only what it has: a blank label would say a field was
  // there and is hidden from it.
  const shown = (value: unknown) => !buyerLogin || (value !== '' && value !== null && value !== undefined);
  const [returning, setReturning] = useState(false);

  // Remember the record for the command bar's "Recently viewed": its id
  // only, and only once it has loaded, so it is one this person can see.
  const loadedId = data?.id ?? null;
  useEffect(() => {
    if (loadedId) pushRecentLead(loadedId);
  }, [loadedId]);
  const redistribute = useRedistribute();

  // `null` means no dialog is open. Closing it is the only way to not act:
  // the Alpine app gates a bulk return on `prompt() || 'default'`, so
  // cancelling the prompt still returns every selected lead. Cancel must
  // cancel, especially now a return reverses money.
  const [confirming, setConfirming] = useState<null | 'redistribute'>(null);
  const [newDeal, setNewDeal] = useState(false);
  const perms = useCrmPermissions();
  const navigate = useNavigate();

  // Filled fields first, so the ones with something to read are not buried
  // under a run of blanks.
  // Keys a lead property already shows, labelled and typed, are not
  // listed again under Custom fields.
  const typed = new Set((data?.properties ?? []).filter((p) => p.raw !== '').map((p) => p.key));
  const customEntries = data
    ? Object.entries((data.custom_data ?? {}) as Record<string, unknown>)
        .filter(([k]) => !SHOWN_ABOVE.has(k.toLowerCase()) && !typed.has(k))
        .sort(
        ([, a], [, b]) => Number(fieldText(a) === '') - Number(fieldText(b) === ''),
      )
    : [];
  const name = data ? data.fullname || `${data.firstname} ${data.lastname}`.trim() : '';
  const sold = data?.distribution.sold ?? false;
  // The row's stream: its type for the header ("Energy Premium · lead"),
  // and whether it sells. A row on a stream that has never sold, and that
  // made nothing, shows no money at all.
  const stream = useStreamOf()(data?.campaign_id);
  const rowTypeShown = !!stream && showsRowType(stream.rowType);
  const money = !stream?.does || showsMoney(stream.does) || (data?.revenue ?? 0) > 0;
  const alreadyReturned = data?.delivery_status === 'returned';

  function close() {
    setConfirming(null);
  }

  async function doRedistribute() {
    if (!id) return;
    try {
      const r = await redistribute.mutateAsync({ lead_ids: [id] });
      notifications.show({
        color: r.queued > 0 ? 'teal' : 'orange',
        title: r.queued > 0 ? 'Queued for redistribution' : 'Not queued',
        message:
          r.errors.length > 0
            ? r.errors.join('; ')
            : `The distribution worker runs next; the outcome will appear on this ${v.one('lead')} shortly.`,
      });
      close();
    } catch (e) {
      // 409: another redistribute of this lead is still running. Nothing
      // went wrong; this one was simply not queued on top of it.
      if (e instanceof ApiError && e.status === 409) {
        notifications.show({
          color: 'orange',
          title: 'Already in progress',
          message: `This ${v.one('lead')} is already being redistributed. Its outcome will appear here shortly.`,
        });
        return;
      }
      notifications.show({ color: 'red', title: 'Redistribute failed', message: (e as Error).message });
    }
  }

  return (
    <Modal
      opened={id !== null}
      onClose={onClose}
      size="xl"
      title={`${v.One('lead')} detail`}
      styles={{ title: { fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' } }}
    >
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && id && <Group justify="center" p="xl"><Loader /></Group>}

      {data && (
        /* Who the lead is and what it made, before any tab: the two things
           someone opening a lead almost always wants first. */
        <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
          <div style={{ minWidth: 0 }}>
            {rowTypeShown && (
              <div data-testid="row-detail-eyebrow" style={{ marginBottom: 4 }}>
                <MonoLabel>{`${data.campaign_name} \u00b7 ${stream.label}`}</MonoLabel>
              </div>
            )}
            <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', color: TOKENS.text }}>
              {name || data.email || `Unnamed ${v.one('lead')}`}
            </div>
            <Group gap={8} mt={6} wrap="wrap">
              <Badge
                variant="light"
                color={data.delivery_status === 'delivered' ? 'chartGreen' : 'gray'}
              >
                {titleCase(data.delivery_status || data.status || 'unknown')}
              </Badge>
              {data.score_detail && (
                <ScoreBadge score={data.score_detail.total} band={data.score_detail.band} withLabel />
              )}
              <MonoLabel>Received {formatDateTime(data.created_at)}</MonoLabel>
            </Group>
          </div>
          {money && (
          <div style={{ textAlign: 'right', flex: 'none' }} data-testid="row-detail-money">
            <MonoLabel>{alreadyReturned ? 'Returned' : sold ? 'Revenue' : `Not ${v.p.sold}`}</MonoLabel>
            <div
              style={{
                ...TYPE.display, fontSize: 26, marginTop: 4,
                color: sold && !alreadyReturned ? TOKENS.text : TOKENS.textMuted,
              }}
            >
              {formatGBP(data.revenue)}
            </div>
          </div>
          )}
        </Group>
      )}

      {data && (
        <Tabs defaultValue="detail">
          <Tabs.List mb="md">
            <Tabs.Tab value="detail">Detail</Tabs.Tab>
            <Tabs.Tab value="distribution">
              Distribution{data.attempts.length > 0 && ` (${data.attempts.length})`}
            </Tabs.Tab>
            {perms.seesTimeline && <Tabs.Tab value="timeline">Timeline</Tabs.Tab>}
            {seesReturns(navUser) && <Tabs.Tab value="returns">Returns</Tabs.Tab>}
            <Tabs.Tab value="raw">Raw request</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="detail">
            {/* Payout is left out: it is never written, so it read £0.00 on
                every lead. Status is shown once, from delivery_status, unless
                the lead status says something different. */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <FieldGroup title={v.One('person')}>
                {shown(name) && <Field label="Name" value={name} />}
                {shown(data.phone1) && <Field label="Phone" value={data.phone1} />}
                {shown(data.distribution.phone_check) && (
                  <Field label="Phone check" value={<PhoneCheckValue check={data.distribution.phone_check} />} wide />
                )}
                {shown(data.email) && <Field label="Email" value={data.email} wide />}
                {shown(data.company) && <Field label="Company" value={data.company} wide />}
              </FieldGroup>
              <FieldGroup title="Routing">
                <Field label={v.One('campaign')} value={data.campaign_name} />
                {shown(data.source) && <Field label={v.p.sourceTag} value={data.source} />}
                {shown(data.supplier_name || data.supplier_id) && (
                  <Field label={v.One('supplier')} value={data.supplier_name || data.supplier_id} />
                )}
                {money && <Field label={v.One('buyer')} value={data.buyer_name || data.buyer_id} />}
                {data.status && data.status !== data.delivery_status && (
                  <Field label={`${v.One('lead')} status`} value={titleCase(data.status)} />
                )}
              </FieldGroup>
            </SimpleGrid>

            <SubmissionProperties properties={data.properties ?? []} />

            {customEntries.length > 0 && (
              <div style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 10, padding: '12px 14px 14px', marginTop: 16 }}>
                <Group justify="space-between" mb={10}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Custom fields</div>
                  <MonoLabel>{customEntries.length} fields</MonoLabel>
                </Group>
                <ScrollArea.Autosize mah={260} type="auto">
                  <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md" verticalSpacing={12}>
                    {customEntries.map(([key, value]) => (
                      <Field key={key} label={key} value={fieldText(value)} />
                    ))}
                  </SimpleGrid>
                </ScrollArea.Autosize>
              </div>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="distribution">
            <Group gap="xl" mb="md">
              <Figure label={v.Many('routeGroup')} value={data.distribution.total_groups} />
              <Figure label="Attempts" value={data.distribution.total_attempts} />
              <Figure label="Total time" value={`${data.distribution.total_time_ms}ms`} />
              <Figure
                label={v.p.Sold}
                value={data.distribution.sold ? formatGBP(data.revenue) : 'No'}
                positive={data.distribution.sold}
              />
            </Group>

            {data.distribution.duplicate_info && (
              <Alert color="orange" variant="light" mb="md" title="Rejected as a duplicate">
                <Text size="sm">
                  Matched on <b>{data.distribution.duplicate_info.matched_field ?? 'an unknown field'}</b>
                  {data.distribution.duplicate_info.previous_lead_date && (
                    <>{` against ${v.a('lead')} received ${formatDateTime(data.distribution.duplicate_info.previous_lead_date)}`}</>
                  )}
                  {data.distribution.duplicate_info.previous_buyer_name && (
                    <>{` and ${v.p.sold} ${v.p.soldPrep} ${data.distribution.duplicate_info.previous_buyer_name}`}</>
                  )}
                  .
                </Text>
              </Alert>
            )}

            {data.score_detail && (
              <div
                style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}
                data-testid="lead-score"
              >
                <Group justify="space-between" mb={6}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>How it was scored</div>
                  <ScoreBadge score={data.score_detail.total} band={data.score_detail.band} withLabel />
                </Group>
                <Text size="xs" c="dimmed" mb={6}>
                  {`Worked out when it arrived, before routing. ${v.One('routeGroup')} and ${v.one('route')} filters can use it.`}
                </Text>
                {data.score_detail.matched.length === 0 ? (
                  <Text size="sm">No rule matched.</Text>
                ) : (
                  <Stack gap={2}>
                    {data.score_detail.matched.map((m) => (
                      <Group key={m.rule_id} gap="xs" wrap="nowrap">
                        <Text size="sm" w={44} ta="right" ff="monospace">{signed(m.points)}</Text>
                        <Text size="sm">{m.name}</Text>
                      </Group>
                    ))}
                  </Stack>
                )}
                {data.score_detail.errors.length > 0 && (
                  <Text size="xs" c="dimmed" mt={6}>
                    {`Not counted, so worth 0: ${data.score_detail.errors.map((e) => `${e.name} (${e.reason.replace(/\.$/, '')})`).join('; ')}.`}
                  </Text>
                )}
                {data.score_detail.unavailable && (
                  <Text size="xs" c="dimmed" mt={6}>{data.score_detail.unavailable}</Text>
                )}
              </div>
            )}

            {data.distribution.routing.length > 0 && (
              <div style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>How it was routed</div>
                <Stack gap={6}>
                  {/* The score's own line is the card above. */}
                  {data.distribution.routing
                    .filter((line) => line !== data.score_detail?.summary)
                    .map((line, i) => (
                      <Text key={i} size="sm">{line}</Text>
                    ))}
                </Stack>
              </div>
            )}

            {data.attempts.length === 0 ? (
              // Genuinely nothing recorded, as opposed to "screened out before
              // routing" — those now arrive as attempts with a reason attached.
              <Alert color="gray" variant="light" title="Never distributed">
                <Text size="sm">
                  {`No distribution was recorded for this ${v.one('lead')}. That usually means it was refused when it arrived, before any ${v.one('routeGroup')} was considered, or it arrived through an import that does not distribute. Its delivery status is `}
                  <b>{data.delivery_status || data.status || 'unknown'}</b>.
                </Text>
              </Alert>
            ) : (
              <Timeline active={data.attempts.length} bulletSize={22} lineWidth={2}>
                {data.attempts.map((a, idx) => {
                  const o = outcome(a.result, v);
                  const Icon = o.icon;
                  return (
                    <Timeline.Item
                      key={`${a.route_id ?? a.group_id ?? 'step'}-${idx}`}
                      bullet={<Icon size={13} />}
                      color={o.colour}
                      title={
                        <Group gap="xs">
                          <Badge color={o.colour} variant="light" size="sm">{o.label}</Badge>
                          <Text fw={600} size="sm">
                            {a.route_name ?? a.group_name ?? 'On arrival'}
                          </Text>
                          {a.buyer_name && <Text size="sm" c="dimmed">→ {a.buyer_name}</Text>}
                        </Group>
                      }
                    >
                      {/* The server's sentence names the cap, filter or reason;
                          the generic blurb is only for steps it can't word. */}
                      {a.reason ? (
                        <Text size="sm" mb={4}>{a.reason}</Text>
                      ) : (
                        o.blurb && <Text size="sm" c="dimmed" mb={4}>{o.blurb}</Text>
                      )}

                      {a.filter_reason && (!a.reason || a.result === 'validation_failed') && (
                        <Alert color="gray" variant="light" p="xs" mb={6}>
                          <Text size="sm">{plainPhoneReason(a.filter_reason)}</Text>
                        </Alert>
                      )}
                      {a.error_message && !a.reason && (
                        <Alert color="red" variant="light" p="xs" mb={6}>
                          <Text size="sm">{plainPhoneReason(a.error_message)}</Text>
                        </Alert>
                      )}

                      <Group gap="lg" mt={4}>
                        {a.price != null && (
                          <Text size="xs" c="dimmed">Price <b>{formatGBP(a.price)}</b></Text>
                        )}
                        {a.delivery_method && (
                          <Text size="xs" c="dimmed">Method <b>{a.delivery_method}</b></Text>
                        )}
                        {a.response_time_ms > 0 && (
                          <Text size="xs" c="dimmed">Took <b>{a.response_time_ms}ms</b></Text>
                        )}
                        {a.group_name && a.route_name && (
                          <Text size="xs" c="dimmed">{v.One('routeGroup')} <b>{a.group_name}</b></Text>
                        )}
                      </Group>

                      <Payloads sent={a.sent_data} response={a.response_body} />
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            )}

            {data.distribution.sold_to.length > 0 && (
              <>
                <Divider my="md" />
                <Text size="sm" c="dimmed">
                  {`${v.p.Sold} ${v.p.soldPrep}: ${data.distribution.sold_to.join(', ')}`}
                </Text>
              </>
            )}
          </Tabs.Panel>

          {perms.seesTimeline && (
            /* Everything that happened to this lead, newest first: arrived,
               routed (the same words as How it was routed), redistributes,
               returns and emails, with the notes, tasks and calls on it. */
            <Tabs.Panel value="timeline">
              <TimelinePanel objectType="submission" objectId={data.id} />
            </Tabs.Panel>
          )}

          {seesReturns(navUser) && (
            <Tabs.Panel value="returns">
              <LeadReturnsPanel leadId={data.id} />
            </Tabs.Panel>
          )}

          <Tabs.Panel value="raw">
            {data.raw_request == null ? (
              <Alert color="gray" variant="light" title="No raw request stored">
                <Text size="sm">
                  {`This ${v.one('lead')} has no recorded inbound payload. ${v.Many('lead')} created by CSV import do not have one, and neither do ${v.many('lead')} submitted before request capture was added.`}
                </Text>
              </Alert>
            ) : (
              <ScrollArea.Autosize mah={520}>
                <Code block>{JSON.stringify(data.raw_request, null, 2)}</Code>
              </ScrollArea.Autosize>
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      {data && (
        <>
          <Divider my="md" />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {alreadyReturned
                ? `This ${v.one('lead')} has already been returned.`
                : sold
                  ? `${v.p.Sold} for ${formatGBP(data.revenue)}.`
                  : `Not ${v.p.sold}.`}
            </Text>
            <Group gap="sm">
              {perms.seesDeals && perms.editDeals && (
                <Button
                  variant="default"
                  leftSection={<IconBriefcase size={16} />}
                  onClick={() => setNewDeal(true)}
                >
                  {`Create ${v.one('deal')}`}
                </Button>
              )}
              <Button
                variant="default"
                leftSection={<IconRefresh size={16} />}
                onClick={() => setConfirming('redistribute')}
              >
                Redistribute
              </Button>
              {/* Hidden, not disabled, on a stream that has never sold
                  when the row made nothing: there is no sale to return. */}
              {returnsStaff && money && (
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconArrowBackUp size={16} />}
                  disabled={!sold || alreadyReturned}
                  onClick={() => setReturning(true)}
                >
                  {`Return ${v.one('lead')}`}
                </Button>
              )}
              {buyerLogin && (
                <Button
                  variant="light"
                  leftSection={<IconArrowBackUp size={16} />}
                  disabled={!sold || alreadyReturned}
                  onClick={() => setReturning(true)}
                >
                  Request a return
                </Button>
              )}
            </Group>
          </Group>
        </>
      )}

      {data && (
        <NewDealModal
          opened={newDeal}
          onClose={() => setNewDeal(false)}
          prefill={{
            name: name || data.company || data.email || '',
            sourceSubmissionId: data.id,
          }}
          onCreated={(dealId) => {
            setNewDeal(false);
            onClose();
            navigate(`/deals?deal=${encodeURIComponent(dealId)}`);
          }}
        />
      )}

      <ReturnDialog
        target={id ? { kind: 'lead', leadId: id } : null}
        opened={returning}
        asBuyer={buyerLogin}
        onClose={() => setReturning(false)}
      />

      <Modal
        opened={confirming !== null}
        onClose={close}
        title={`Redistribute this ${v.one('lead')}?`}
        size="md"
        zIndex={1001}
      >

        {confirming === 'redistribute' && data && (
          <Stack>
            {sold && !alreadyReturned && (
              <Alert color="orange" variant="light" title={`This ${v.one('lead')} has already ${v.p.soldPerfect}`}>
                <Text size="sm">
                  {`It ${v.p.soldPast} for ${formatGBP(data.revenue)}${data.buyer_name ? ` ${v.p.soldPrep} ${data.buyer_name}` : ''}. Redistributing sends it through routing again, which may sell it a second time.`}
                </Text>
              </Alert>
            )}
            <Text size="sm">
              {`The ${v.one('lead')} goes back on the distribution queue. The worker runs next, so the outcome will not be known immediately.`}
            </Text>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={close}>Cancel</Button>
              <Button loading={redistribute.isPending} onClick={doRedistribute}>
                Redistribute
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Modal>
  );
}
